import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight } from 'lucide-react';

const values = [
  {
    t: 'Senior or nothing',
    d: 'No junior cover. The engineer on the call is the engineer on the keyboard.',
  },
  {
    t: 'Boring on purpose',
    d: 'We prefer mature tools, clear interfaces, and the dullest solution that works.',
  },
  {
    t: 'Evaluation first',
    d: 'If we can\'t measure it, we don\'t ship it — especially for AI features.',
  },
  {
    t: 'Hand-over is the deliverable',
    d: 'Code your future team can extend, docs they\'ll actually read.',
  },
];

const timeline = [
  { y: '2014', t: 'Founded in Sydney as a two-engineer consultancy.' },
  { y: '2017', t: 'First cross-platform product shipped across iOS, Android & web from one codebase.' },
  { y: '2021', t: 'Built our AI integration practice around early GPT-3 deployments.' },
  { y: '2024', t: 'Launched the AI security & training practices in response to client demand.' },
  { y: '2026', t: 'Forty-plus production systems running on Veventex-built foundations.' },
];

export default function About() {
  return (
    <div data-testid="about-page">
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pt-20 lg:pt-28 pb-16">
        <div className="font-mono text-[10px] tracking-widest uppercase text-ink-muted mb-4">
          About
        </div>
        <h1 className="font-display font-bold tracking-tighter text-5xl md:text-7xl text-ink max-w-5xl leading-[0.95]">
          A studio built around<br />
          <span className="italic font-light">craft, not headcount.</span>
        </h1>
        <p className="mt-8 text-lg md:text-xl text-ink-muted max-w-3xl leading-relaxed">
          Veventex is a small, deliberately senior software engineering studio based
          in Sydney. We partner with founders, in-house teams and enterprises across
          Australia and abroad to ship cross-platform products and trustworthy AI
          systems — and to help their teams keep building once we leave.
        </p>
      </section>

      <section className="border-y border-black/10">
        <div className="grid grid-cols-1 md:grid-cols-2">
          <div className="aspect-[4/3] md:aspect-auto overflow-hidden">
            <img
              src="https://images.pexels.com/photos/6804068/pexels-photo-6804068.jpeg?w=1600"
              alt="Veventex engineering team"
              className="w-full h-full object-cover"
            />
          </div>
          <div className="p-10 md:p-16 bg-cream-alt">
            <div className="font-mono text-[10px] tracking-widest uppercase text-ink-muted mb-4">
              Our position
            </div>
            <h2 className="font-display font-bold tracking-tighter text-3xl md:text-5xl text-ink leading-tight">
              We sit between strategy and shipping.
            </h2>
            <p className="mt-6 text-ink-muted leading-relaxed">
              Big consultancies make decks. Freelancers ship code. We do both — but
              with a bias toward the working product. Every engagement ends with
              software in production and a team that can extend it.
            </p>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24 lg:py-32">
        <div className="font-mono text-[10px] tracking-widest uppercase text-ink-muted mb-4">
          Values
        </div>
        <h2 className="font-display font-bold tracking-tighter text-4xl md:text-6xl text-ink max-w-3xl mb-12">
          Four principles we don&apos;t bend on.
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-black/10 border border-black/10">
          {values.map((v, i) => (
            <div key={v.t} className="bg-cream p-8" data-testid={`value-card-${i}`}>
              <div className="font-mono text-xs tracking-widest text-ember mb-4">
                0{i + 1}
              </div>
              <div className="font-display font-semibold text-xl text-ink mb-2">{v.t}</div>
              <div className="text-ink-muted leading-relaxed">{v.d}</div>
            </div>
          ))}
        </div>
      </section>

      <section className="border-t border-black/10 bg-cream-alt">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24 lg:py-32 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <div className="font-mono text-[10px] tracking-widest uppercase text-ink-muted mb-4">
              Timeline
            </div>
            <h2 className="font-display font-bold tracking-tighter text-4xl md:text-5xl text-ink">
              Eleven years of<br />shipping software.
            </h2>
          </div>
          <ol className="lg:col-span-8 border-l border-black/15 space-y-10 pl-8">
            {timeline.map((t) => (
              <li key={t.y} className="relative">
                <span className="absolute -left-[34px] top-2 h-2 w-2 bg-ember rounded-full" />
                <div className="font-mono text-xs tracking-widest text-ink-muted mb-2">{t.y}</div>
                <div className="text-lg text-ink leading-relaxed">{t.t}</div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24 lg:py-32">
        <div className="border border-black/10 p-10 md:p-16 bg-cream flex flex-col md:flex-row md:items-end justify-between gap-8">
          <div>
            <div className="font-mono text-[10px] tracking-widest uppercase text-ink-muted mb-4">
              Work with us
            </div>
            <h2 className="font-display font-bold tracking-tighter text-3xl md:text-5xl text-ink max-w-2xl">
              We take on a handful of projects each quarter.
            </h2>
          </div>
          <Link
            to="/contact"
            data-testid="about-cta-button"
            className="inline-flex items-center gap-2 bg-ink text-cream px-8 py-4 text-sm font-medium hover:bg-ember transition-colors whitespace-nowrap"
          >
            Start a conversation
            <ArrowUpRight size={16} />
          </Link>
        </div>
      </section>
    </div>
  );
}
